import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { notification } from "antd";
import { AxiosError } from "axios";
import { axiosInstance } from "../util/axios";
import { AcceptInviteDto, GetInviteInfoDto, UserSummary } from "../types/user";
import { useNavigate } from "react-router-dom";
import { TanstackRetry } from "../types/tanstack-properties.types";

export const useGetUserOverview = () =>
  useQuery<UserSummary[]>({
    queryKey: ["userOverview"],
    queryFn: () => axiosInstance.get("/user/overview").then((w) => w.data),
    initialData: [],
  });

export const useDeleteUser = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => axiosInstance.delete(`/user/${userId}`),
    onSuccess: () => {
      notification.success({
        message: t("toast.user-removed"),
        description: t("toast.user-removed-description"),
      });
      return queryClient.invalidateQueries({ queryKey: ["userOverview"] });
    },
    onError: (error: AxiosError<AxiosError>) => {
      let description = t("toast.please-check-your-credentials");

      switch (error.response?.data.message) {
        case "Cannot delete yourself":
          description = t("toast.cannot-delete-yourself");
          break;
        case "Cannot delete main user":
          description = t("toast.cannot-delete-owner");
          break;
      }

      notification.error({
        message: t("toast.failed-to-remove-user"),
        description,
      });
    },
  });
};

export const useAddUser = () => {
  const { t } = useTranslation();
  // const navigate = useNavigate(); /* Add this when the mailing client is implemented */
  return useMutation({
    mutationFn: (data: { email: string }) =>
      axiosInstance.post(`/user/invite`, data),
    onSuccess: () => {
      notification.success({
        message: t("toast.user-invited"),
        description: t("toast.user-invited-description"),
      });
      // navigate("/portal/users"); /* Add this when the mailing client is implemented */
    },
    onError: (error: AxiosError<AxiosError>) => {
      if (error.response?.data.message) {
        notification.error({
          message: t("toast.failed-to-invite-user"),
          description: t("toast.user-already-exists"),
        });
        return;
      }
      notification.error({
        message: t("toast.failed-to-invite-user"),
        description: t("toast.try-again-later"),
      });
    },
  });
};

export const useGetAcceptInviteInfo = (
  inviteToken: string | undefined,
  retry: TanstackRetry,
) =>
  useQuery<GetInviteInfoDto>({
    queryKey: ["acceptInviteInfo", inviteToken],
    queryFn: () =>
      axiosInstance
        .get(`/user/accept-invite/${inviteToken}`)
        .then((w) => w.data),
    enabled: !!inviteToken,
    retry: retry,
    initialData: {
      email: "",
      companyName: "",
    },
  });

export const useAcceptInvite = (inviteToken: string) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (data: AcceptInviteDto) =>
      axiosInstance.post(`/user/accept-invite/${inviteToken}`, data),
    onSuccess: () => {
      notification.success({
        message: t("toast.invite-accepted"),
        description: t("toast.invite-accepted-description"),
      });
      navigate("/login");
    },
    onError: () => {
      notification.error({
        message: t("toast.failed-to-accept-invite"),
        description: t("toast.please-check-your-credentials"),
      });
    },
  });
};
